/**
 * Before/after comparison for sections/wc-before-after.liquid.
 * The full-size native <input type="range"> provides drag, tap/click-to-jump and keyboard control;
 * this script mirrors its value into --wc-ba-pos, keeps aria-valuetext readable, wires the
 * Before / Compare / After snap buttons (non-drag alternative, WCAG 2.5.7) and plays one gentle
 * hint sweep the first time the slider scrolls into view (skipped for reduced motion).
 */
const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class WcBeforeAfter extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;
  /** @type {number | undefined} */
  #easeTimer;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const range = this.range;
    if (!range) return;

    range.addEventListener('input', () => this.#set(Number(range.value), false), { signal });
    this.addEventListener('click', this.#handleSnap, { signal });
    this.#set(Number(range.value), false);

    if (!reduceMotion()) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry?.isIntersecting) return;
          observer.disconnect();
          this.#hint();
        },
        { threshold: 0.5 }
      );
      observer.observe(this.frame ?? this);
      signal.addEventListener('abort', () => observer.disconnect());
    }
  }

  disconnectedCallback() {
    this.#controller?.abort();
    window.clearTimeout(this.#easeTimer);
  }

  get range() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('[data-wc-ba-range]'));
  }

  get frame() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-wc-ba-frame]'));
  }

  /**
   * @param {number} value - % of the frame showing "before" (0 = all after, 100 = all before)
   * @param {boolean} ease - animate to the new position
   */
  #set(value, ease) {
    const frame = this.frame;
    const range = this.range;
    if (!frame || !range) return;
    const clamped = Math.max(0, Math.min(100, value));

    if (ease && !reduceMotion()) {
      frame.classList.add('is-easing');
      window.clearTimeout(this.#easeTimer);
      this.#easeTimer = window.setTimeout(() => frame.classList.remove('is-easing'), 750);
    } else {
      frame.classList.remove('is-easing');
    }

    frame.style.setProperty('--wc-ba-pos', `${clamped}%`);
    range.value = String(clamped);
    range.setAttribute('aria-valuetext', (range.dataset.template ?? '[before]%').replace('[before]', String(Math.round(clamped))));

    for (const button of this.querySelectorAll('[data-wc-ba-snap]')) {
      button.setAttribute('aria-pressed', String(Number(/** @type {HTMLElement} */ (button).dataset.wcBaSnap) === Math.round(clamped)));
    }
  }

  /** @param {MouseEvent} event */
  #handleSnap = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-wc-ba-snap]') : null;
    if (!(button instanceof HTMLElement)) return;
    this.#set(Number(button.dataset.wcBaSnap), true);
  };

  /** One soft sweep so shoppers see the image is interactive. */
  #hint() {
    const start = Number(this.range?.value ?? 50);
    const steps = [Math.min(start + 18, 92), Math.max(start - 14, 8), start];
    steps.forEach((value, index) => {
      window.setTimeout(() => {
        if (this.frame?.matches(':hover, :focus-within')) return; // never fight the user
        this.#set(value, true);
      }, 300 + index * 750);
    });
  }
}

if (!customElements.get('wc-before-after')) customElements.define('wc-before-after', WcBeforeAfter);
